import { apiFetch } from 'api/client';

// In-page Authentik login. stats-api drives Authentik's flow executor and
// answers with one of these statuses:
//   challenge     - render `challenge` (identification, password, MFA code, consent)
//   authenticated - signed in; the body is the same user payload as /authenticate
//   fallback      - this sign-in needs Authentik's own page; use the redirect login
//   denied        - Authentik refused the sign-in
//   expired       - the attempt timed out; start again
//   error         - transient failure (Authentik unreachable, invalid step...)

async function postFlow(path, json) {
    let response;
    try {
        response = await apiFetch(path, { method: 'POST', json, skipSessionCheck: true });
    } catch {
        return { status: 'error', error: 'Could not reach the server. Check your connection.' };
    }

    let data = {};
    try {
        data = await response.json();
    } catch {
        data = {};
    }
    if (response.status === 429) {
        return { status: 'error', error: data.error || 'Too many sign-in attempts. Try again shortly.' };
    }
    if (!data.status) {
        const fallback = response.ok
            ? 'Sign-in failed. Please try again.'
            : `Sign-in request was rejected (HTTP ${response.status}). Please try again.`;
        return { status: 'error', error: data.error || fallback };
    }
    return data;
}

export function startAuthentikLogin() {
    return postFlow('/authentik/login/start', {});
}

export function submitAuthentikStep(payload) {
    return postFlow('/authentik/login/step', payload);
}

export function cancelAuthentikLogin() {
    return postFlow('/authentik/login/cancel', {});
}

// Older backends without /auth-methods only support the redirect login.
const LEGACY_METHODS = { sso: { enabled: true, inline: false }, local: { enabled: true } };

export async function fetchAuthMethods() {
    try {
        const response = await apiFetch('/auth-methods', { skipSessionCheck: true });
        if (!response.ok) {
            return LEGACY_METHODS;
        }
        const data = await response.json();
        return {
            sso: { ...LEGACY_METHODS.sso, ...(data?.sso || {}) },
            local: { ...LEGACY_METHODS.local, ...(data?.local || {}) },
        };
    } catch {
        return LEGACY_METHODS;
    }
}

export const DEVICE_LABELS = {
    totp: { label: 'Authenticator app', icon: 'smartphone', hint: 'Enter the 6-digit code from your authenticator app.' },
    static: { label: 'Recovery code', icon: 'key', hint: 'Enter one of your saved recovery codes.' },
    email: { label: 'Email code', icon: 'mail', hint: 'Enter the code we sent to your email.' },
    sms: { label: 'SMS code', icon: 'sms', hint: 'Enter the code we sent to your phone.' },
};

// Devices whose code is only sent after the device is selected.
export const DEVICES_REQUIRING_SELECTION = new Set(['email', 'sms']);

export function identificationLabel(userFields = []) {
    const fields = new Set(userFields);
    if (fields.has('username') && fields.has('email')) return 'Username or email';
    if (fields.has('email')) return 'Email';
    return 'Username';
}

export function fieldErrors(challenge, field) {
    return challenge?.response_errors?.[field] || [];
}
